import React, { FC, useState } from 'react';
import { useFormikContext } from 'formik';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, makeStyles } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';

import { Alert, Loaders } from 'components';
import { useDeleteAssignmentMutation } from 'api';
import { useNotify, useProtectedClient } from 'hooks';
import * as paths from 'paths';

interface Props {
  working: boolean;
  createForm?: boolean;
  id?: string;
}

const useStyles = makeStyles(theme => ({
  loaderButton: {
    position: 'relative',
  },
  submit: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

const Actions: FC<Props> = ({ createForm, working, id }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const client = useProtectedClient();
  const history = useHistory();
  const notify = useNotify();
  const { dirty, isValid, submitForm } = useFormikContext();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { mutate: deleteAssignment } = useDeleteAssignmentMutation({
    onError: notify.mutationError,
    onSuccess: onSuccessMutation,
  });

  function onSuccessMutation() {
    history.push(paths.adminSettingsAssignments);
    notify.info({ message: t('admin:assignmentDeleteMessage') });
  }

  function renderButtonText() {
    if (createForm) {
      return t('common:create');
    }

    return t('common:save');
  }

  function onClickDeleteToggle() {
    setIsDeleteOpen(!isDeleteOpen);
  }

  function handleDelete() {
    deleteAssignment({
      id,
      client_id: client.id,
    });
  }

  return (
    <Box mt={2}>
      <Grid container justify={createForm ? 'flex-end' : 'space-between'} alignItems="center">
        {!createForm && (
          <Grid item xs={6}>
            <Button startIcon={<DeleteIcon />} onClick={onClickDeleteToggle}>
              {t('admin:assignmentDelete')}
            </Button>

            <Alert
              destructive
              open={isDeleteOpen}
              onConfirm={handleDelete}
              onCancel={onClickDeleteToggle}
              message={t('admin:assignmentDeleteAlert')}
              confirm={t('common:delete')}
            />
          </Grid>
        )}

        <Grid item xs={6} container justify="flex-end">
          <Grid item>
            <Box mr={4}>
              <Button variant="contained" size="large" onClick={() => history.goBack()}>
                {t('common:cancel')}
              </Button>
            </Box>
          </Grid>

          <Grid item className={classes.loaderButton}>
            <Button
              onClick={submitForm}
              size="large"
              disabled={!isValid || !dirty || working}
              type="submit"
              variant="contained"
              color="primary"
              className={classes.submit}
            >
              {renderButtonText()}
            </Button>
            {working && <Loaders.Button size={24} />}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Actions;
